/**
 * Example data for the ehrtslib Format Converter demo app
 *
 * These examples demonstrate different openEHR RM structures in various formats.
 * Ehrlibs model examples (primary): https://github.com/Ehrlibs/openEHR-model-examples
 */

/** Formats an instance preset may ship as editor payload. */
export type ExamplePayloadFormat =
  | "flat"
  | "structured"
  | "json"
  | "yaml"
  | "xml";

const EXAMPLE_PAYLOAD_FORMATS: ExamplePayloadFormat[] = [
  "flat",
  "structured",
  "json",
  "yaml",
  "xml",
];

export type DemoExample = {
  name: string;
  description: string;
  preferredFormat?: ExamplePayloadFormat;
  flat?: string;
  structured?: string;
  json?: string;
  yaml?: string;
  xml?: string;
};

/**
 * Pick format + payload for a preset without leaving the UI on a mismatched
 * input format (e.g. FLAT selected while only canonical JSON exists → null YAML).
 */
export function resolveExampleInput(
  example: DemoExample,
  currentFormat?: string,
): { format: ExamplePayloadFormat; payload: string } {
  const preferred = example.preferredFormat;
  const candidates: string[] = [];
  if (preferred) candidates.push(preferred);
  if (currentFormat) candidates.push(currentFormat);
  for (const format of EXAMPLE_PAYLOAD_FORMATS) {
    if (!candidates.includes(format)) candidates.push(format);
  }

  for (const format of candidates) {
    if (!EXAMPLE_PAYLOAD_FORMATS.includes(format as ExamplePayloadFormat)) {
      continue;
    }
    const payload = example[format as ExamplePayloadFormat];
    if (typeof payload === "string" && payload.length > 0) {
      return { format: format as ExamplePayloadFormat, payload };
    }
  }

  throw new Error(`Example "${example.name}" has no usable payload`);
}

export const EXAMPLES = {
  /**
   * FLAT / STRUCTURED instance for the Ehrlibs "Accident report including vital signs"
   * template (`templateId` / root node id: accident_report_including_vital_signs).
   * Paths follow the published Web Template; load the matching `.t.json` on AD@git
   * for round-trip conversion. Pattern aligned with
   * openehr://examples/flat/vital_signs_blood_pressure and
   * openehr://guides/simplified_formats/*.
   */
  "accident-report-vitals": {
    name: "Accident report + vital signs (Ehrlibs FLAT)",
    description:
      "FLAT composition for Ehrlibs theme-pack template Accident report including vital signs",
    preferredFormat: "flat",
    flat: `{
  "ctx/language": "en",
  "ctx/territory": "SE",
  "ctx/time": "2026-08-02T10:15:00Z",
  "ctx/composer_name": "Dr. A. Smith",
  "ctx/category|code": "433",
  "ctx/category|value": "event",
  "ctx/category|terminology": "openehr",
  "ctx/setting|code": "225",
  "ctx/setting|value": "secondary medical care",
  "ctx/setting|terminology": "openehr",

  "accident_report_including_vital_signs/problem_diagnosis:0/injury": "Ankle sprain during marathon",
  "accident_report_including_vital_signs/problem_diagnosis:0/clinical_description": "Twisted left ankle at kilometre 18; able to weight-bear with pain",
  "accident_report_including_vital_signs/problem_diagnosis:0/anatomical_location:0/specific_site": "Lateral ankle ligaments",
  "accident_report_including_vital_signs/problem_diagnosis:0/date_time_of_onset": "2026-08-02T09:45:00Z",
  "accident_report_including_vital_signs/problem_diagnosis:0/sport_event:0/event_name": "Stockholm Marathon 2026",
  "accident_report_including_vital_signs/problem_diagnosis:0/sport_event:0/time_after_start": "PT1H45M",
  "accident_report_including_vital_signs/problem_diagnosis:0/sport_event:0/location:0/address_or_description": "Near water station, km 18",

  "accident_report_including_vital_signs/vital_signs/pulse_oximetry:0/any_event:0/spo|numerator": 98,
  "accident_report_including_vital_signs/vital_signs/pulse_oximetry:0/any_event:0/spo|denominator": 100,
  "accident_report_including_vital_signs/vital_signs/pulse_oximetry:0/any_event:0/spo|type": 2,
  "accident_report_including_vital_signs/vital_signs/pulse_oximetry:0/any_event:0/time": "2026-08-02T10:10:00Z",

  "accident_report_including_vital_signs/vital_signs/respiration:0/any_event:0/rate|magnitude": 18,
  "accident_report_including_vital_signs/vital_signs/respiration:0/any_event:0/rate|unit": "/min",
  "accident_report_including_vital_signs/vital_signs/respiration:0/any_event:0/time": "2026-08-02T10:10:00Z",

  "accident_report_including_vital_signs/vital_signs/pulse_heart_beat:0/any_event:0/rate|magnitude": 92,
  "accident_report_including_vital_signs/vital_signs/pulse_heart_beat:0/any_event:0/rate|unit": "/min",
  "accident_report_including_vital_signs/vital_signs/pulse_heart_beat:0/any_event:0/time": "2026-08-02T10:10:00Z"
}`,
    structured: `{
  "ctx": {
    "language": "en",
    "territory": "SE",
    "time": "2026-08-02T10:15:00Z",
    "composer_name": "Dr. A. Smith",
    "category": [
      { "|code": "433", "|value": "event", "|terminology": "openehr" }
    ],
    "setting": [
      { "|code": "225", "|value": "secondary medical care", "|terminology": "openehr" }
    ]
  },
  "accident_report_including_vital_signs": {
    "problem_diagnosis": [{
      "injury": [{ "|value": "Ankle sprain during marathon" }],
      "clinical_description": [{ "|value": "Twisted left ankle at kilometre 18; able to weight-bear with pain" }],
      "anatomical_location": [{
        "specific_site": [{ "|value": "Lateral ankle ligaments" }]
      }],
      "date_time_of_onset": [{ "|value": "2026-08-02T09:45:00Z" }],
      "sport_event": [{
        "event_name": [{ "|value": "Stockholm Marathon 2026" }],
        "time_after_start": [{ "|value": "PT1H45M" }],
        "location": [{
          "address_or_description": [{ "|value": "Near water station, km 18" }]
        }]
      }]
    }],
    "vital_signs": [{
      "pulse_oximetry": [{
        "any_event": [{
          "spo": [{ "|numerator": 98, "|denominator": 100, "|type": 2 }],
          "time": [{ "|value": "2026-08-02T10:10:00Z" }]
        }]
      }],
      "respiration": [{
        "any_event": [{
          "rate": [{ "|magnitude": 18, "|unit": "/min" }],
          "time": [{ "|value": "2026-08-02T10:10:00Z" }]
        }]
      }],
      "pulse_heart_beat": [{
        "any_event": [{
          "rate": [{ "|magnitude": 92, "|unit": "/min" }],
          "time": [{ "|value": "2026-08-02T10:10:00Z" }]
        }]
      }]
    }]
  }
}`,
    json: `{
  "_type": "COMPOSITION",
  "name": { "_type": "DV_TEXT", "value": "Accident report including vital signs" },
  "archetype_node_id": "openEHR-EHR-COMPOSITION.encounter.v1",
  "language": {
    "_type": "CODE_PHRASE",
    "terminology_id": { "_type": "TERMINOLOGY_ID", "value": "ISO_639-1" },
    "code_string": "en"
  },
  "territory": {
    "_type": "CODE_PHRASE",
    "terminology_id": { "_type": "TERMINOLOGY_ID", "value": "ISO_3166-1" },
    "code_string": "SE"
  },
  "category": {
    "_type": "DV_CODED_TEXT",
    "value": "event",
    "defining_code": {
      "_type": "CODE_PHRASE",
      "terminology_id": { "_type": "TERMINOLOGY_ID", "value": "openehr" },
      "code_string": "433"
    }
  },
  "composer": { "_type": "PARTY_IDENTIFIED", "name": "Dr. A. Smith" },
  "context": {
    "_type": "EVENT_CONTEXT",
    "start_time": { "_type": "DV_DATE_TIME", "value": "2026-08-02T10:15:00Z" },
    "setting": {
      "_type": "DV_CODED_TEXT",
      "value": "secondary medical care",
      "defining_code": {
        "_type": "CODE_PHRASE",
        "terminology_id": { "_type": "TERMINOLOGY_ID", "value": "openehr" },
        "code_string": "225"
      }
    }
  },
  "content": []
}`,
    xml: `<?xml version="1.0" encoding="UTF-8"?>
<COMPOSITION xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance">
  <name><value>Accident report including vital signs</value></name>
  <archetype_node_id>openEHR-EHR-COMPOSITION.encounter.v1</archetype_node_id>
  <language>
    <terminology_id><value>ISO_639-1</value></terminology_id>
    <code_string>en</code_string>
  </language>
  <territory>
    <terminology_id><value>ISO_3166-1</value></terminology_id>
    <code_string>SE</code_string>
  </territory>
  <category>
    <value>event</value>
    <defining_code>
      <terminology_id><value>openehr</value></terminology_id>
      <code_string>433</code_string>
    </defining_code>
  </category>
  <composer xsi:type="PARTY_IDENTIFIED"><name>Dr. A. Smith</name></composer>
</COMPOSITION>`,
    yaml: `_type: COMPOSITION
name:
  _type: DV_TEXT
  value: Accident report including vital signs
archetype_node_id: openEHR-EHR-COMPOSITION.encounter.v1
language: ISO_639-1::en
territory: ISO_3166-1::SE
category: openehr::433|event|
composer:
  name: Dr. A. Smith`,
  },

  'dv-text': {
    name: 'Simple DV_TEXT',
    description: 'A simple text value',
    preferredFormat: "json",
    json: `{
  "_type": "DV_TEXT",
  "value": "Hello, openEHR!"
}`,
    xml: `<?xml version="1.0" encoding="UTF-8"?>
<DV_TEXT xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance">
  <value>Hello, openEHR!</value>
</DV_TEXT>`,
    yaml: `_type: DV_TEXT
value: Hello, openEHR!`
  },

  'code-phrase': {
    name: 'CODE_PHRASE',
    description: 'A coded term reference',
    preferredFormat: "json",
    json: `{
  "_type": "CODE_PHRASE",
  "terminology_id": {
    "_type": "TERMINOLOGY_ID",
    "value": "ISO_639-1"
  },
  "code_string": "en"
}`,
    xml: `<?xml version="1.0" encoding="UTF-8"?>
<CODE_PHRASE xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance">
  <terminology_id>
    <value>ISO_639-1</value>
  </terminology_id>
  <code_string>en</code_string>
</CODE_PHRASE>`,
    yaml: `_type: CODE_PHRASE
terminology_id:
  _type: TERMINOLOGY_ID
  value: ISO_639-1
code_string: en`
  },

  'dv-coded-text': {
    name: 'DV_CODED_TEXT',
    description: 'Text with coded terminology',
    preferredFormat: "json",
    json: `{
  "_type": "DV_CODED_TEXT",
  "value": "event",
  "defining_code": {
    "_type": "CODE_PHRASE",
    "terminology_id": {
      "_type": "TERMINOLOGY_ID",
      "value": "openehr"
    },
    "code_string": "433"
  }
}`,
    xml: `<?xml version="1.0" encoding="UTF-8"?>
<DV_CODED_TEXT xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance">
  <value>event</value>
  <defining_code>
    <terminology_id>
      <value>openehr</value>
    </terminology_id>
    <code_string>433</code_string>
  </defining_code>
</DV_CODED_TEXT>`,
    yaml: `_type: DV_CODED_TEXT
value: event
defining_code:
  _type: CODE_PHRASE
  terminology_id:
    _type: TERMINOLOGY_ID
    value: openehr
  code_string: '433'`
  },

  'basic-composition': {
    name: 'Basic COMPOSITION',
    description: 'Simple composition structure',
    preferredFormat: "json",
    json: `{
  "_type": "COMPOSITION",
  "name": {"_type": "DV_TEXT", "value": "Vital Signs Encounter"},
  "archetype_node_id": "openEHR-EHR-COMPOSITION.encounter.v1",
  "language": {"_type": "CODE_PHRASE", "terminology_id": {"_type": "TERMINOLOGY_ID", "value": "ISO_639-1"}, "code_string": "en"},
  "territory": {"_type": "CODE_PHRASE", "terminology_id": {"_type": "TERMINOLOGY_ID", "value": "ISO_3166-1"}, "code_string": "GB"},
  "category": {"_type": "DV_CODED_TEXT", "value": "event", "defining_code": {"_type": "CODE_PHRASE", "terminology_id": {"_type": "TERMINOLOGY_ID", "value": "openehr"}, "code_string": "433"}},
  "composer": {"_type": "PARTY_IDENTIFIED", "name": "Dr. Smith"}
}`,
    xml: `<?xml version="1.0" encoding="UTF-8"?>
<COMPOSITION xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance">
  <name xsi:type="DV_TEXT">
    <value>Vital Signs Encounter</value>
  </name>
  <archetype_node_id>openEHR-EHR-COMPOSITION.encounter.v1</archetype_node_id>
  <language>
    <terminology_id>
      <value>ISO_639-1</value>
    </terminology_id>
    <code_string>en</code_string>
  </language>
  <territory>
    <terminology_id>
      <value>ISO_3166-1</value>
    </terminology_id>
    <code_string>GB</code_string>
  </territory>
  <category xsi:type="DV_CODED_TEXT">
    <value>event</value>
    <defining_code>
      <terminology_id>
        <value>openehr</value>
      </terminology_id>
      <code_string>433</code_string>
    </defining_code>
  </category>
  <composer xsi:type="PARTY_IDENTIFIED">
    <name>Dr. Smith</name>
  </composer>
</COMPOSITION>`,
    yaml: `_type: COMPOSITION
name:
  _type: DV_TEXT
  value: Vital Signs Encounter
archetype_node_id: openEHR-EHR-COMPOSITION.encounter.v1
language:
  _type: CODE_PHRASE
  terminology_id:
    _type: TERMINOLOGY_ID
    value: ISO_639-1
  code_string: en
territory:
  _type: CODE_PHRASE
  terminology_id:
    _type: TERMINOLOGY_ID
    value: ISO_3166-1
  code_string: GB
category:
  _type: DV_CODED_TEXT
  value: event
  defining_code:
    _type: CODE_PHRASE
    terminology_id:
      _type: TERMINOLOGY_ID
      value: openehr
    code_string: '433'
composer:
  _type: PARTY_IDENTIFIED
  name: Dr. Smith`
  },

  'complex-composition': {
    name: 'Complex COMPOSITION',
    description: 'Composition with nested content',
    preferredFormat: "json",
    json: `{
  "_type": "COMPOSITION",
  "name": {"_type": "DV_TEXT", "value": "Vital Signs Encounter"},
  "archetype_node_id": "openEHR-EHR-COMPOSITION.encounter.v1",
  "language": {"_type": "CODE_PHRASE", "terminology_id": {"_type": "TERMINOLOGY_ID", "value": "ISO_639-1"}, "code_string": "en"},
  "territory": {"_type": "CODE_PHRASE", "terminology_id": {"_type": "TERMINOLOGY_ID", "value": "ISO_3166-1"}, "code_string": "GB"},
  "category": {"_type": "DV_CODED_TEXT", "value": "event", "defining_code": {"_type": "CODE_PHRASE", "terminology_id": {"_type": "TERMINOLOGY_ID", "value": "openehr"}, "code_string": "433"}},
  "composer": {"_type": "PARTY_IDENTIFIED", "name": "Dr. Smith"},
  "content": [{
    "_type": "SECTION",
    "name": {"_type": "DV_TEXT", "value": "Vital Signs"},
    "items": [
      {
        "_type": "ELEMENT",
        "name": {"_type": "DV_TEXT", "value": "Diagnosis"},
        "value": {
          "_type": "DV_CODED_TEXT",
          "value": "Diabetes mellitus type 2",
          "defining_code": {
            "_type": "CODE_PHRASE",
            "terminology_id": {"_type": "TERMINOLOGY_ID", "value": "SNOMED-CT"},
            "code_string": "44054006"
          }
        }
      },
      {
        "_type": "ELEMENT",
        "name": {"_type": "DV_TEXT", "value": "Pulse rate"},
        "value": {"_type": "DV_QUANTITY", "magnitude": 72, "units": "/min"}
      }
    ]
  }]
}`,
    xml: `<?xml version="1.0" encoding="UTF-8"?>
<COMPOSITION xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance">
  <name xsi:type="DV_TEXT">
    <value>Vital Signs Encounter</value>
  </name>
  <archetype_node_id>openEHR-EHR-COMPOSITION.encounter.v1</archetype_node_id>
  <language>
    <terminology_id>
      <value>ISO_639-1</value>
    </terminology_id>
    <code_string>en</code_string>
  </language>
  <territory>
    <terminology_id>
      <value>ISO_3166-1</value>
    </terminology_id>
    <code_string>GB</code_string>
  </territory>
  <category xsi:type="DV_CODED_TEXT">
    <value>event</value>
    <defining_code>
      <terminology_id>
        <value>openehr</value>
      </terminology_id>
      <code_string>433</code_string>
    </defining_code>
  </category>
  <composer xsi:type="PARTY_IDENTIFIED">
    <name>Dr. Smith</name>
  </composer>
  <content xsi:type="SECTION">
    <name xsi:type="DV_TEXT">
      <value>Vital Signs</value>
    </name>
    <items xsi:type="ELEMENT">
      <name xsi:type="DV_TEXT">
        <value>Diagnosis</value>
      </name>
      <value xsi:type="DV_CODED_TEXT">
        <value>Diabetes mellitus type 2</value>
        <defining_code>
          <terminology_id>
            <value>SNOMED-CT</value>
          </terminology_id>
          <code_string>44054006</code_string>
        </defining_code>
      </value>
    </items>
    <items xsi:type="ELEMENT">
      <name xsi:type="DV_TEXT">
        <value>Pulse rate</value>
      </name>
      <value xsi:type="DV_QUANTITY">
        <magnitude>72</magnitude>
        <units>/min</units>
      </value>
    </items>
  </content>
</COMPOSITION>`,
    yaml: `_type: COMPOSITION
name:
  _type: DV_TEXT
  value: Vital Signs Encounter
archetype_node_id: openEHR-EHR-COMPOSITION.encounter.v1
language:
  _type: CODE_PHRASE
  terminology_id:
    _type: TERMINOLOGY_ID
    value: ISO_639-1
  code_string: en
territory:
  _type: CODE_PHRASE
  terminology_id:
    _type: TERMINOLOGY_ID
    value: ISO_3166-1
  code_string: GB
category:
  _type: DV_CODED_TEXT
  value: event
  defining_code:
    _type: CODE_PHRASE
    terminology_id:
      _type: TERMINOLOGY_ID
      value: openehr
    code_string: '433'
composer:
  _type: PARTY_IDENTIFIED
  name: Dr. Smith
content:
  - _type: SECTION
    name:
      _type: DV_TEXT
      value: Vital Signs
    items:
      - _type: ELEMENT
        name:
          _type: DV_TEXT
          value: Diagnosis
        value:
          _type: DV_CODED_TEXT
          value: Diabetes mellitus type 2
          defining_code:
            _type: CODE_PHRASE
            terminology_id:
              _type: TERMINOLOGY_ID
              value: SNOMED-CT
            code_string: '44054006'
      - _type: ELEMENT
        name:
          _type: DV_TEXT
          value: Pulse rate
        value:
          _type: DV_QUANTITY
          magnitude: 72
          units: /min`
  },

  'realistic-composition': {
    name: 'Realistic Template Composition',
    description: 'Real-world example (ChemoForm-MBA) with Clusters and Observations',
    preferredFormat: "json",
    json: `{
  "_type": "COMPOSITION",
  "name": {
    "_type": "DV_TEXT",
    "value": "ChemoForm-MBA.v7"
  },
  "uid": {
    "_type": "OBJECT_VERSION_ID",
    "value": "573b2f9c-d267-4052-ae09-7b58dcfd6233::regionstockholm_se::1"
  },
  "archetype_details": {
    "_type": "ARCHETYPED",
    "archetype_id": {
      "_type": "ARCHETYPE_ID",
      "value": "openEHR-EHR-COMPOSITION.self_reported_data.v1"
    },
    "template_id": {
      "_type": "TEMPLATE_ID",
      "value": "ChemoForm-MBA.v7"
    },
    "rm_version": "1.1.0"
  },
  "feeder_audit": {
    "_type": "FEEDER_AUDIT",
    "original_content": {
      "_type": "DV_PARSABLE",
      "value": "{}",
      "formalism": "application/json"
    },
    "originating_system_audit": {
      "_type": "FEEDER_AUDIT_DETAILS",
      "system_id": "FormRenderer"
    }
  },
  "archetype_node_id": "openEHR-EHR-COMPOSITION.self_reported_data.v1",
  "language": {
    "_type": "CODE_PHRASE",
    "terminology_id": {
      "_type": "TERMINOLOGY_ID",
      "value": "ISO_639-1"
    },
    "code_string": "sv"
  },
  "territory": {
    "_type": "CODE_PHRASE",
    "terminology_id": {
      "_type": "TERMINOLOGY_ID",
      "value": "ISO_3166-1"
    },
    "code_string": "SI"
  },
  "category": {
    "_type": "DV_CODED_TEXT",
    "value": "event",
    "defining_code": {
      "_type": "CODE_PHRASE",
      "terminology_id": {
        "_type": "TERMINOLOGY_ID",
        "value": "openehr"
      },
      "code_string": "433"
    }
  },
  "composer": {
    "_type": "PARTY_IDENTIFIED",
    "name": "erik.sundvall@regionstockholm.se"
  },
  "context": {
    "_type": "EVENT_CONTEXT",
    "start_time": {
      "_type": "DV_DATE_TIME",
      "value": "2023-08-31T18:31:16.004+02:00"
    },
    "setting": {
      "_type": "DV_CODED_TEXT",
      "value": "other care",
      "defining_code": {
        "_type": "CODE_PHRASE",
        "terminology_id": {
          "_type": "TERMINOLOGY_ID",
          "value": "openehr"
        },
        "code_string": "238"
      }
    },
    "other_context": {
      "_type": "ITEM_TREE",
      "name": {
        "_type": "DV_TEXT",
        "value": "Item tree"
      },
      "archetype_node_id": "at0003",
      "items": [
        {
          "_type": "CLUSTER",
          "name": {
            "_type": "DV_TEXT",
            "value": "Vårdenhet"
          },
          "archetype_details": {
            "_type": "ARCHETYPED",
            "archetype_id": {
              "_type": "ARCHETYPE_ID",
              "value": "openEHR-EHR-CLUSTER.organisation.v1"
            },
            "rm_version": "1.1.0"
          },
          "archetype_node_id": "openEHR-EHR-CLUSTER.organisation.v1",
          "items": [
            {
              "_type": "ELEMENT",
              "name": {
                "_type": "DV_TEXT",
                "value": "Namn"
              },
              "archetype_node_id": "at0001",
              "value": {
                "_type": "DV_TEXT",
                "value": "Brandbergens vårdcentral"
              }
            },
            {
              "_type": "ELEMENT",
              "name": {
                "_type": "DV_TEXT",
                "value": "Identifierare"
              },
              "archetype_node_id": "at0003",
              "value": {
                "_type": "DV_IDENTIFIER",
                "id": "SE2321000016-1003",
                "type": "urn:oid:1.2.752.29.4.19"
              }
            },
            {
              "_type": "ELEMENT",
              "name": {
                "_type": "DV_TEXT",
                "value": "Roll"
              },
              "archetype_node_id": "at0004",
              "value": {
                "_type": "DV_CODED_TEXT",
                "value": "vårdenhet",
                "defining_code": {
                  "_type": "CODE_PHRASE",
                  "terminology_id": {
                    "_type": "TERMINOLOGY_ID",
                    "value": "http://snomed.info/sct/900000000000207008"
                  },
                  "code_string": "43741000"
                }
              }
            },
            {
              "_type": "CLUSTER",
              "name": {
                "_type": "DV_TEXT",
                "value": "Vårdgivare"
              },
              "archetype_details": {
                "_type": "ARCHETYPED",
                "archetype_id": {
                  "_type": "ARCHETYPE_ID",
                  "value": "openEHR-EHR-CLUSTER.organisation.v1"
                },
                "rm_version": "1.1.0"
              },
              "archetype_node_id": "openEHR-EHR-CLUSTER.organisation.v1",
              "items": [
                {
                  "_type": "ELEMENT",
                  "name": {
                    "_type": "DV_TEXT",
                    "value": "Namn"
                  },
                  "archetype_node_id": "at0001",
                  "value": {
                    "_type": "DV_TEXT",
                    "value": "Stockholms läns sjukvårdsområde"
                  }
                },
                {
                  "_type": "ELEMENT",
                  "name": {
                    "_type": "DV_TEXT",
                    "value": "Identifierare"
                  },
                  "archetype_node_id": "at0003",
                  "value": {
                    "_type": "DV_IDENTIFIER",
                    "id": "SE2321000016-2GJS",
                    "type": "urn:oid:1.2.752.29.4.19"
                  }
                },
                {
                  "_type": "ELEMENT",
                  "name": {
                    "_type": "DV_TEXT",
                    "value": "Organisationsnummer"
                  },
                  "archetype_node_id": "at0003",
                  "value": {
                    "_type": "DV_IDENTIFIER",
                    "id": "2232084",
                    "type": "urn:oid:2.5.4.97"
                  }
                },
                {
                  "_type": "ELEMENT",
                  "name": {
                    "_type": "DV_TEXT",
                    "value": "Roll"
                  },
                  "archetype_node_id": "at0004",
                  "value": {
                    "_type": "DV_CODED_TEXT",
                    "value": "vårdgivare",
                    "defining_code": {
                      "_type": "CODE_PHRASE",
                      "terminology_id": {
                        "_type": "TERMINOLOGY_ID",
                        "value": "http://snomed.info/sct/45991000052106"
                      },
                      "code_string": "143591000052106"
                    }
                  }
                }
              ]
            }
          ]
        }
      ]
    }
  },
  "content": [
    {
      "_type": "OBSERVATION",
      "name": {
        "_type": "DV_TEXT",
        "value": "Frågeformulär för symptom och andra tecken"
      },
      "archetype_details": {
        "_type": "ARCHETYPED",
        "archetype_id": {
          "_type": "ARCHETYPE_ID",
          "value": "openEHR-EHR-OBSERVATION.symptom_sign_screening.v1"
        },
        "rm_version": "1.1.0"
      },
      "archetype_node_id": "openEHR-EHR-OBSERVATION.symptom_sign_screening.v1",
      "language": {
        "_type": "CODE_PHRASE",
        "terminology_id": {
          "_type": "TERMINOLOGY_ID",
          "value": "ISO_639-1"
        },
        "code_string": "sv"
      },
      "encoding": {
        "_type": "CODE_PHRASE",
        "terminology_id": {
          "_type": "TERMINOLOGY_ID",
          "value": "IANA_character-sets"
        },
        "code_string": "UTF-8"
      },
      "subject": {
        "_type": "PARTY_SELF"
      },
      "data": {
        "_type": "HISTORY",
        "name": {
          "_type": "DV_TEXT",
          "value": "History"
        },
        "archetype_node_id": "at0001",
        "origin": {
          "_type": "DV_DATE_TIME",
          "value": "2023-08-31T18:31:16.004+02:00"
        },
        "events": [
          {
            "_type": "POINT_EVENT",
            "name": {
              "_type": "DV_TEXT",
              "value": "Ospecificerad händelse"
            },
            "archetype_node_id": "at0002",
            "time": {
              "_type": "DV_DATE_TIME",
              "value": "2023-08-31T18:31:16.004+02:00"
            },
            "data": {
              "_type": "ITEM_TREE",
              "name": {
                "_type": "DV_TEXT",
                "value": "Tree"
              },
              "archetype_node_id": "at0003",
              "items": [
                {
                  "_type": "ELEMENT",
                  "name": {
                    "_type": "DV_TEXT",
                    "value": "Screeningssyfte"
                  },
                  "archetype_node_id": "at0034",
                  "value": {
                    "_type": "DV_CODED_TEXT",
                    "value": "utvärdering av cytostatikabehandling",
                    "defining_code": {
                      "_type": "CODE_PHRASE",
                      "terminology_id": {
                        "_type": "TERMINOLOGY_ID",
                        "value": "SNOMED-CT"
                      },
                      "code_string": "385785003"
                    }
                  }
                },
                {
                  "_type": "CLUSTER",
                  "name": {
                    "_type": "DV_TEXT",
                    "value": "Trötthet"
                  },
                  "archetype_details": {
                    "_type": "ARCHETYPED",
                    "archetype_id": {
                      "_type": "ARCHETYPE_ID",
                      "value": "openEHR-EHR-CLUSTER.specific_symptom_sign_question.v0"
                    },
                    "rm_version": "1.1.0"
                  },
                  "archetype_node_id": "openEHR-EHR-CLUSTER.specific_symptom_sign_question.v0",
                  "items": [
                    {
                      "_type": "ELEMENT",
                      "name": {
                        "_type": "DV_TEXT",
                        "value": "Symptom/tecken-benämning"
                      },
                      "archetype_node_id": "at0004",
                      "value": {
                        "_type": "DV_CODED_TEXT",
                        "value": "trötthet (fatigue)",
                        "defining_code": {
                          "_type": "CODE_PHRASE",
                          "terminology_id": {
                            "_type": "TERMINOLOGY_ID",
                            "value": "SNOMED-CT"
                          },
                          "code_string": "84229001"
                        }
                      }
                    },
                    {
                      "_type": "ELEMENT",
                      "name": {
                        "_type": "DV_TEXT",
                        "value": "Upplever du trötthet (fatigue) som påverkar ditt dagliga liv?"
                      },
                      "archetype_node_id": "at0005",
                      "value": {
                        "_type": "DV_CODED_TEXT",
                        "value": "Ja",
                        "defining_code": {
                          "_type": "CODE_PHRASE",
                          "terminology_id": {
                            "_type": "TERMINOLOGY_ID",
                            "value": "local"
                          },
                          "code_string": "at0023"
                        }
                      }
                    },
                    {
                      "_type": "CLUSTER",
                      "name": {
                        "_type": "DV_TEXT",
                        "value": "följdfråga"
                      },
                      "archetype_details": {
                        "_type": "ARCHETYPED",
                        "archetype_id": {
                          "_type": "ARCHETYPE_ID",
                          "value": "openEHR-EHR-CLUSTER.followup_question.v0"
                        },
                        "rm_version": "1.1.0"
                      },
                      "archetype_node_id": "openEHR-EHR-CLUSTER.followup_question.v0",
                      "items": [
                        {
                          "_type": "ELEMENT",
                          "name": {
                            "_type": "DV_TEXT",
                            "value": "Hur påverkar tröttheten ditt dagliga liv?"
                          },
                          "archetype_node_id": "at0002",
                          "value": {
                            "_type": "DV_ORDINAL",
                            "value": 2,
                            "symbol": {
                              "_type": "DV_CODED_TEXT",
                              "value": "Måttlig trötthet, vilar mindre än 50% av vaken tid",
                              "defining_code": {
                                "_type": "CODE_PHRASE",
                                "terminology_id": {
                                  "_type": "TERMINOLOGY_ID",
                                  "value": "local"
                                },
                                "code_string": "at0.17"
                              }
                            }
                          }
                        }
                      ]
                    }
                  ]
                }
              ]
            }
          }
        ]
      }
    }
  ]
}`
  },
};
